import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { MainLayout } from 'src/layouts/main';
import { SimpleLayout } from 'src/layouts/simple';

import { SplashScreen } from 'src/components/loading-screen';

// auth
import { AuthGuard } from 'src/auth/guard';

// ----------------------------------------------------------------------

const FaqsPage = lazy(() => import('src/pages/faqs'));
const AboutPage = lazy(() => import('src/pages/about-us'));
const ContactPage = lazy(() => import('src/pages/contact-us'));
const PricingPage = lazy(() => import('src/pages/pricing'));
const PaymentPage = lazy(() => import('src/pages/payment'));
const ComingSoonPage = lazy(() => import('src/pages/coming-soon'));
const MaintenancePage = lazy(() => import('src/pages/maintenance'));
// Product
const ProductListPage = lazy(() => import('src/pages/product/list'));
const ProductDetailsPage = lazy(() => import('src/pages/product/details'));
const ProductCheckoutPage = lazy(() => import('src/pages/product/checkout'));
// Blog
const PostListPage = lazy(() => import('src/pages/post/list'));
const PostDetailsPage = lazy(() => import('src/pages/post/details'));
// Error
const Page500 = lazy(() => import('src/pages/error/500'));
const Page403 = lazy(() => import('src/pages/error/403'));
const Page404 = lazy(() => import('src/pages/error/404'));
// Blank
const BlankPage = lazy(() => import('src/pages/blank'));
// ASSET
const AssetView = lazy(() => import('src/pages/qrcode/details'));

// ----User----

// ----------Dashboard-------------
const UserDashBoards = lazy(() => import('src/pages/user-dashboard/index'));

// ----------ProfilePage-------------
const ProfilePage = lazy(() => import('src/pages/profile/details'));

// ----------users assets-------------------
const UserAssetsListPage = lazy(() => import('src/pages/user-assets/list'));
const UserAssetsNewPage = lazy(() => import('src/pages/user-assets/new'));
const UserAssetsViewPage = lazy(() => import('src/pages/user-assets/details'));
const UserAssetsEditPage = lazy(() => import('src/pages/user-assets/edit'));

// ----------user-tickets-------------------
const UserTicketsListPage = lazy(() => import('src/pages/users-tickets/list'));
const GeneralTicketNewEdit = lazy(() => import('src/pages/users-tickets/general-ticket-new'));
const UserTicketsEditPage = lazy(() => import('src/pages/users-tickets/edit'));
const UserTicketNewEditPage = lazy(() => import('src/pages/users-tickets/new'));
const UserTicketsNewEditPage = lazy(() => import('src/pages/user-assets/ticket-new'));
const UserTicketsViewPage = lazy(() => import('src/pages/users-tickets/details'));

// ----------user-periodic-check-------------------
const UserPeriodicCheckListPage = lazy(() => import('src/pages/user-periodic-check/list'));

// -----------asset audit detail page for external users for asset verification-------------
const AssetAuditDetailView = lazy(() => import('src/pages/asset-audit/public-details'));
const AssetAuditStartVerificationView = lazy(
  () => import('src/pages/asset-audit/public-verification')
);

// ----------------------------------------------------------------------

export const mainRoutes = [
  {
    element: (
      <Suspense fallback={<SplashScreen />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        element: (
          <MainLayout>
            <Outlet />
          </MainLayout>
        ),
        children: [
          {
            path: 'about-us',
            element: <AboutPage />,
          },

          {
            path: 'qrcode/view/:id',
            element: <AssetView />,
          },

          {
            path: 'asset-audit/view-temp/:id',
            element: <AssetAuditDetailView />,
          },

          {
            path: 'asset-audit/start-verification-temp/:id',
            element: <AssetAuditStartVerificationView />,
          },

          {
            path: 'contact-us',
            element: <ContactPage />,
          },
          {
            path: 'faqs',
            element: <FaqsPage />,
          },
          {
            path: 'blank',
            element: <BlankPage />,
          },
          {
            path: 'user',
            children: [
              {
                element: (
                  <AuthGuard>
                    <UserDashBoards />
                  </AuthGuard>
                ),
                index: true,
              },
              { path: 'dashboard', element: <UserDashBoards /> },
              { path: 'profile', element: <ProfilePage /> },
              // ----------ASSETS----------
              { path: 'user-assets', element: <UserAssetsListPage /> },
              { path: 'user-assets/new-request', element: <UserAssetsNewPage /> },
              { path: 'user-assets/view/:id', element: <UserAssetsViewPage /> },
              { path: 'user-assets/edit-request/:id', element: <UserAssetsEditPage /> },
              // ----------TICKETS---------
              { path: 'tickets-list', element: <UserTicketsListPage /> },
              { path: 'general-ticket/new', element: <GeneralTicketNewEdit /> },
              { path: 'raise-ticket', element: <UserTicketsNewEditPage /> },
              { path: 'raise-ticket/:id', element: <UserTicketsEditPage /> },
              { path: 'tickets/view/:id', element: <UserTicketsViewPage /> },
              { path: 'new-ticket/:id', element: <UserTicketNewEditPage /> },
              // ----------PERIODIC-CHECK---------
              { path: 'periodic-checklist', element: <UserPeriodicCheckListPage /> },
            ],
          },
          {
            path: 'product',
            children: [
              { element: <ProductListPage />, index: true },
              { path: 'list', element: <ProductListPage /> },
              { path: ':id', element: <ProductDetailsPage /> },
              { path: 'checkout', element: <ProductCheckoutPage /> },
            ],
          },
          {
            path: 'post',
            children: [
              { element: <PostListPage />, index: true },
              { path: 'list', element: <PostListPage /> },
              { path: ':title', element: <PostDetailsPage /> },
            ],
          },
        ],
      },
      {
        path: 'pricing',
        element: (
          <SimpleLayout>
            <PricingPage />
          </SimpleLayout>
        ),
      },
      {
        path: 'payment',
        element: (
          <SimpleLayout>
            <PaymentPage />
          </SimpleLayout>
        ),
      },
      {
        path: 'coming-soon',
        element: (
          <SimpleLayout content={{ compact: true }}>
            <ComingSoonPage />
          </SimpleLayout>
        ),
      },
      {
        path: 'maintenance',
        element: (
          <SimpleLayout content={{ compact: true }}>
            <MaintenancePage />
          </SimpleLayout>
        ),
      },
      { path: '500', element: <Page500 /> },
      { path: '404', element: <Page404 /> },
      { path: '403', element: <Page403 /> },
    ],
  },
];
